import { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { InputField } from '@clay-run/glaze/input-field';
import { Button } from '@clay-run/glaze/button';
import { Checkbox } from '@clay-run/glaze/checkbox';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@clay-run/glaze/table';
import { Text } from '@clay-run/glaze/text';

import type { CampaignMember } from '../../../_shared/launcher-campaign.types';
import { filterMembersByQuery, reviewRungMembers } from '../../review-step-logic';
import type { PlaybooksMatchedMembersRung } from '../../schemas';
import { sendOutcomeBadges } from '../../send-summary';
import {
  MemberCreatedCell,
  MemberDetailsButton,
  MemberEmailCell,
  MemberNameCell,
  MemberStatusCell,
  memberDisplay,
} from './MemberRowCells';
import { useMemberTest } from './useMemberTest';
import { useTestSelected } from './useTestSelected';
import { memberTestRowOf, pickTestBatch, testBatchNote } from '../../member-test-logic';
import { REP_STATUS_BADGE, classifyRepStatus } from '../../dispatch-rep-status';
import { useSendLogRows } from './dispatch-log/useSendLogRows';
import {
  clearGroupSelection,
  selectionKey,
  setGroupSelection,
  toggleSelected,
  useReviewSelectionStore,
} from '../../review-selection-store';
import { useMemberTestStore, memberTestKey } from '../../member-test-store';

/** Above this many people a folder gets a "Find a person" box (plan 2026-10-15-003 §4.2). */
export const REVIEW_FIND_BOX_THRESHOLD = 20;

export interface ReviewRungMembersProps {
  /** A group's ladder entry, or the bare ids of "Not in any group". `null` while the ladder is unusable. */
  source: Pick<PlaybooksMatchedMembersRung, 'matchedMemberIds'> | readonly string[] | null;
  members: readonly CampaignMember[];
  /** Set while the ladder is loading / failed / stale — the one shared sentence replaces the table. */
  unavailableSentence: string | null;
  /** The roster itself is still loading: say so, instead of reporting people as missing or the folder as empty. */
  membersLoading: boolean;
  /** Shown when the folder has no people at all. */
  emptyText: string;
  /** The person whose side panel is open, for the row tint. */
  selectedMemberId: string | null;
  /** The Preview button and the Name click — both open the person's panel (the U5 drawer hangs off this seam). */
  onOpenMember: (memberId: string) => void;
  /**
   * The play whose Test button each row carries. `undefined` (Playbook mode,
   * where there is no play to test against) renders no Test column at all.
   */
  playId?: string;
  /** `tally.routedMemberIds`; `null` while the ladder is unavailable. */
  routedMemberIds?: string[] | null;
  /** This group's reason a test cannot be sent (one sentence, also the button's hover), or `null`. */
  testDisabledReason?: string | null;
  /**
   * This folder's key in `review-selection-store` (the rung's key). Present = the
   * rows carry checkboxes and a ticked person shows the "Test selected" bar;
   * absent ("Not in any group", Playbook mode) = no checkboxes.
   */
  groupKey?: string;
}

const NO_TICKS: readonly string[] = [];

/**
 * The people under one Review folder — same columns the old flat "Campaign
 * members" table had (Name, Email, CM status, CM created date, Preview), rendered
 * through the cells `MembersPanel` also uses. View state (the find text) is
 * plain `useState`: per open folder, fine to reset.
 */
export function ReviewRungMembers({
  source,
  members,
  unavailableSentence,
  membersLoading,
  emptyText,
  selectedMemberId,
  onOpenMember,
  playId,
  routedMemberIds = null,
  testDisabledReason = null,
  groupKey,
}: ReviewRungMembersProps) {
  const [query, setQuery] = useState('');
  const { rows, missingCount } = useMemo(() => reviewRungMembers(source, members), [source, members]);
  const visible = useMemo(() => filterMembersByQuery(rows, query), [rows, query]);
  const selectable = playId !== undefined && groupKey !== undefined;
  const ticked = useReviewSelectionStore((state) =>
    selectable ? (state.byKey[selectionKey(playId, groupKey)] ?? NO_TICKS) : NO_TICKS,
  );
  // Ticks on someone no longer in the folder (a re-check moved them) are not counted or sent.
  const tickedSet = useMemo(() => {
    const inFolder = new Set(rows.map((member) => member.id));
    return new Set(ticked.filter((id) => inFolder.has(id)));
  }, [ticked, rows]);
  const visibleTicked = visible.filter((member) => tickedSet.has(member.id)).length;
  const allVisibleTicked = visible.length > 0 && visibleTicked === visible.length;

  if (unavailableSentence !== null) {
    return (
      <div className="p-4">
        <Text size="xs" variant="tertiary">
          {unavailableSentence}
        </Text>
      </div>
    );
  }

  if (membersLoading) {
    return (
      <div className="p-4">
        <Text size="xs" variant="tertiary">
          Loading members…
        </Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-4">
      {missingCount > 0 && (
        <Text size="xs" variant="tertiary">
          {missingCount === 1
            ? '1 matched member is not in the loaded roster'
            : `${missingCount} matched members are not in the loaded roster`}{' '}
          — Re-check to refresh it.
        </Text>
      )}
      {rows.length === 0 && missingCount === 0 && (
        <Text size="sm" variant="tertiary">
          {emptyText}
        </Text>
      )}
      {(rows.length > REVIEW_FIND_BOX_THRESHOLD || query !== '') && (
        <InputField
          type="search"
          size="xs"
          leadingIcon={MagnifyingGlass}
          aria-label="Find a person"
          placeholder="Find a person"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      {selectable && tickedSet.size > 0 && (
        <SelectionBar
          playId={playId}
          groupKey={groupKey}
          folderOrder={rows.map((member) => member.id)}
          selected={tickedSet}
          routedMemberIds={routedMemberIds}
          disabledReason={testDisabledReason}
        />
      )}
      {rows.length > 0 && visible.length === 0 && (
        <Text size="sm" variant="tertiary">
          No one here matches that search.
        </Text>
      )}
      {visible.length > 0 && (
        <div className="max-h-64 overflow-y-auto">
          <Table dense>
            <TableHead>
              <TableRow>
                {selectable && (
                  <TableHeader aria-label="Select">
                    <Checkbox
                      aria-label="Select all in this group"
                      checked={allVisibleTicked}
                      indeterminate={visibleTicked > 0 && !allVisibleTicked}
                      onCheckedChange={() => {
                        // Exactly the people shown (the Find box narrows it); a second press un-ticks them.
                        const shown = new Set(visible.map((member) => member.id));
                        setGroupSelection(
                          playId,
                          groupKey,
                          allVisibleTicked
                            ? [...tickedSet].filter((id) => !shown.has(id))
                            : [...new Set([...tickedSet, ...shown])],
                        );
                      }}
                    />
                  </TableHeader>
                )}
                <TableHeader>Name</TableHeader>
                <TableHeader>Email</TableHeader>
                <TableHeader>CM status</TableHeader>
                <TableHeader>CM created date</TableHeader>
                <TableHeader aria-label="Preview" />
                {playId !== undefined && <TableHeader aria-label="Test" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((member) => {
                const { label, emailCell } = memberDisplay(member);
                const selected = selectedMemberId === member.id;
                return (
                  <TableRow key={member.id} className={selected ? 'bg-bg-selected' : undefined}>
                    {selectable && (
                      <TableCell>
                        <Checkbox
                          aria-label={`Select ${label}`}
                          checked={tickedSet.has(member.id)}
                          onCheckedChange={() => toggleSelected(playId, groupKey, member.id)}
                        />
                      </TableCell>
                    )}
                    <MemberNameCell
                      member={member}
                      label={label}
                      selected={selected}
                      onSelect={() => onOpenMember(member.id)}
                    />
                    <MemberEmailCell emailCell={emailCell} />
                    <MemberStatusCell member={member} />
                    <MemberCreatedCell member={member} />
                    <MemberDetailsButton label={label} variant="preview" onClick={() => onOpenMember(member.id)} />
                    {playId !== undefined && (
                      <MemberTestCell
                        playId={playId}
                        member={member}
                        label={label}
                        routedMemberIds={routedMemberIds}
                        disabledReason={testDisabledReason}
                        onOpenMember={onOpenMember}
                      />
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/**
 * A person row's Test button and result badge (plan 2026-10-15-004 §4.3).
 * Defined HERE, not in `MemberRowCells.tsx`, so `MembersPanel` (Qualify /
 * Sequences) can never pick it up. It runs the same `useMemberTest` the panel
 * does, so both grey at the same moments. A start the server refuses opens the
 * person's panel, where the sentence from the store has room to be read.
 */
function MemberTestCell({
  playId,
  member,
  label,
  routedMemberIds,
  disabledReason,
  onOpenMember,
}: {
  playId: string;
  member: CampaignMember;
  label: string;
  routedMemberIds: string[] | null;
  disabledReason: string | null;
  onOpenMember: (memberId: string) => void;
}) {
  const test = useMemberTest({ playId, member, routedMemberIds, disabledReason });
  const busy = test.pending || test.working;
  // A one-person test's badge is its send summary's one bucket. A "Test selected" batch is one dispatch
  // shared by every ticked person, so its whole-send summary ("2 finished") says nothing about THIS
  // person: their own row (a batch is at most 100 rows, one page) is classified instead.
  const isBatch = (test.testSend?.summary.total ?? 0) > 1;
  const batchLog = useSendLogRows({
    playId,
    dispatchId: test.latest?.dispatchId ?? null,
    status: 'all',
    enabled: isBatch && !busy && test.latest !== undefined,
    live: false,
  });
  const ownRow = isBatch ? memberTestRowOf(batchLog.rows, member.id) : undefined;
  const ownView = ownRow ? classifyRepStatus(ownRow, Date.now()) : null;
  let badge: { status: ReturnType<typeof sendOutcomeBadges>[number]['status']; text: string } | undefined;
  if (test.latest !== undefined && !busy && test.testSend !== undefined) {
    if (!isBatch) badge = sendOutcomeBadges(test.testSend.summary.outcomes)[0];
    else if (ownView !== null) badge = { status: REP_STATUS_BADGE[ownView.status], text: ownView.pillText };
  }

  // The play this cell is showing NOW (null once unmounted). A refusal that
  // lands after the rep moved to another play is kept in the store under the
  // old play's key but must not steal the new play's open panel. Reset at the
  // top of the effect so StrictMode's setup→cleanup→setup leaves it correct.
  const livePlayId = useRef<string | null>(playId);
  useEffect(() => {
    livePlayId.current = playId;
    return () => {
      livePlayId.current = null;
    };
  }, [playId]);

  async function press() {
    const clickedPlayId = playId;
    await test.start();
    // `start` never throws; a refusal is left in the store for this person.
    if (livePlayId.current !== clickedPlayId) return;
    if (useMemberTestStore.getState().failuresByKey[memberTestKey(clickedPlayId, member.id)] !== undefined) {
      onOpenMember(member.id);
    }
  }

  return (
    <TableCell>
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="xs"
          aria-label={`Send a test for ${label}`}
          title={disabledReason ?? undefined}
          disabled={test.disabled}
          onClick={() => void press()}
        >
          {busy && <LoadingSpinner size="sm" />}
          Test
        </Button>
        {badge !== undefined && (
          <button
            type="button"
            aria-label={`Test result for ${label}: ${badge.text}`}
            className="cursor-pointer"
            onClick={() => onOpenMember(member.id)}
          >
            <StatusBadge status={badge.status} size="sm">
              {badge.text}
            </StatusBadge>
          </button>
        )}
      </div>
    </TableCell>
  );
}

/**
 * The bar at the top of a folder while anyone in it is ticked: how many, the
 * "Test selected" button (at most {@link MEMBER_TEST_BATCH_MAX} people per
 * press — the note says so before the press) and Clear.
 */
function SelectionBar({
  playId,
  groupKey,
  folderOrder,
  selected,
  routedMemberIds,
  disabledReason,
}: {
  playId: string;
  groupKey: string;
  folderOrder: readonly string[];
  selected: ReadonlySet<string>;
  routedMemberIds: string[] | null;
  disabledReason: string | null;
}) {
  const batch = pickTestBatch(folderOrder, selected);
  const test = useTestSelected({ playId, groupKey, routedMemberIds, disabledReason, batchIds: batch.ids });
  const note = testBatchNote(batch.selectedCount);
  return (
    <div className="flex flex-wrap items-center gap-2" data-selection-bar="true">
      <Text size="xs" weight="medium">
        {`${batch.selectedCount.toLocaleString('en-US')} selected`}
      </Text>
      <Button
        variant="primary"
        size="xs"
        title={test.reason ?? undefined}
        disabled={test.disabled}
        onClick={() => void test.start(batch.ids)}
      >
        Test selected
      </Button>
      <Button variant="ghost" size="xs" onClick={() => clearGroupSelection(playId, groupKey)}>
        Clear
      </Button>
      {note !== null && (
        <Text size="xs" variant="tertiary">
          {note}
        </Text>
      )}
    </div>
  );
}
